import { useMemo } from "react"
import {
	Bar,
	BarChart,
	CartesianGrid,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import type { ReportingTone } from "@/components/reporting/charts"
import {
	reportingBaselineClassName,
	ReportingChartFrame,
	reportingChartTheme,
	reportingGridClassName,
	toneColor,
} from "@/components/reporting/charts"
import { useReportingBarHoverCursor } from "@/components/reporting/charts/use-dark-mode"
import { api } from "@/lib/api"
import { formatWeekLabel, formatWeekRangeLabel } from "./week-label"

const FEATURES = [
	{ key: "drafts", label: "AI Assistant drafts", tone: "primary" as const },
	{ key: "docsAgent", label: "Docs agent", tone: "categorical-1" as const },
	{
		key: "supportAgent",
		label: "Support agent",
		tone: "secondary" as const,
	},
	{ key: "copilotChat", label: "AI Assistant chat", tone: "warning" as const },
	{ key: "translations", label: "Translations", tone: "muted" as const },
] satisfies Array<{ key: string; label: string; tone: ReportingTone }>

type ChartDatum = {
	date: string
	rangeLabel: string
} & Record<string, string | number>

const usd = (value: number) =>
	`$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`

interface TooltipProps {
	active?: boolean
	payload?: Array<{ payload?: ChartDatum; color?: string }>
	label?: string
}

function CostTooltip({ active, payload, label }: TooltipProps) {
	const datum = payload?.[0]?.payload
	if (!active || !datum) return null
	const heading = String(datum.rangeLabel ?? label ?? "")

	const rows = FEATURES.map((feature) => ({
		...feature,
		cost: Number(datum[feature.key] ?? 0),
		count: Number(datum[`${feature.key}__count`] ?? 0),
		color: toneColor(feature.tone),
	}))
		.filter((row) => row.count > 0)
		.sort((a, b) => b.cost - a.cost)

	if (rows.length === 0) return null

	const total = rows.reduce((sum, row) => sum + row.cost, 0)

	return (
		<div className="rounded-lg bg-white px-3 py-2 text-xs ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			{heading ? (
				<p className="mb-1.5 font-medium text-primary">{heading}</p>
			) : null}
			<ul className="space-y-1">
				{rows.map((row) => (
					<li key={row.key} className="flex items-center gap-2 text-secondary">
						<span
							className="h-2 w-2 shrink-0 rounded-full"
							style={{ backgroundColor: row.color }}
						/>
						<span className="text-tertiary">{row.label}</span>
						<span className="font-medium text-primary">
							~{usd(row.cost)}
							<span className="ml-1 font-normal text-tertiary">
								· {row.count.toLocaleString("en-US")}
							</span>
						</span>
					</li>
				))}
			</ul>
			<p className="mt-1.5 border-t border-gray-200 pt-1.5 font-medium text-primary dark:border-gray-700/80">
				Total ~{usd(total)}
			</p>
		</div>
	)
}

export function AISpendByFeatureChart() {
	const query = api.analytics.aiSpendByFeatureChart.useQuery({})
	const hoverCursor = useReportingBarHoverCursor()

	const chartData = useMemo<ChartDatum[]>(() => {
		if (!query.data) return []
		return query.data.map((item) => {
			const datum: ChartDatum = {
				date: formatWeekLabel(String(item.bucket)),
				rangeLabel: formatWeekRangeLabel(String(item.bucket)),
			}
			item.features.forEach((feature) => {
				datum[feature.key] = feature.cost
				datum[`${feature.key}__count`] = feature.count
			})
			return datum
		})
	}, [query.data])

	return (
		<ReportingChartFrame
			title="Estimated AI cost by feature"
			description="Invocation count times a rough per-call Sonnet estimate, not metered tokens. Drafts count is a floor (overwritten per thread)."
			isLoading={query.isLoading}
			isEmpty={chartData.length === 0}
			legend={
				<div className="flex flex-wrap justify-end gap-x-3 gap-y-1">
					{FEATURES.map((feature) => (
						<div key={feature.key} className="flex items-center gap-1.5">
							<span
								className="h-2 w-2 rounded-full"
								style={{ backgroundColor: toneColor(feature.tone) }}
							/>
							<span className="text-xs text-tertiary">{feature.label}</span>
						</div>
					))}
				</div>
			}
		>
			<div className="h-[220px] w-full [&_*]:outline-none">
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={chartData}
						margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
					>
						<CartesianGrid
							className={reportingGridClassName}
							vertical={false}
						/>
						<ReferenceLine y={0} className={reportingBaselineClassName} />
						<XAxis
							dataKey="date"
							tickLine={false}
							axisLine={false}
							interval="preserveStartEnd"
							tick={{ fontSize: 11 }}
							className="text-xs text-gray-500 dark:text-gray-400"
						/>
						<YAxis
							orientation="right"
							tickLine={false}
							axisLine={false}
							className="text-xs text-gray-500 dark:text-gray-400"
							width={44}
							tickFormatter={(value: number) => usd(value)}
						/>
						<Tooltip cursor={hoverCursor} content={<CostTooltip />} />
						{FEATURES.map((feature, index) => (
							<Bar
								key={feature.key}
								dataKey={feature.key}
								name={feature.label}
								stackId="cost"
								fill={toneColor(feature.tone)}
								maxBarSize={reportingChartTheme.maxBarSize}
								radius={
									index === FEATURES.length - 1
										? [
												reportingChartTheme.barRadius,
												reportingChartTheme.barRadius,
												0,
												0,
											]
										: 0
								}
								isAnimationActive={false}
							/>
						))}
					</BarChart>
				</ResponsiveContainer>
			</div>
		</ReportingChartFrame>
	)
}
